import { useMemo, useState } from 'react'
import {
  IconBuilding,
  IconBuildingWarehouse,
  IconCategory,
  IconConfetti,
  IconLayoutDashboard,
  IconList,
  IconListCheck,
  IconListDetails,
  IconPackages,
  IconSettings2,
  IconShoppingCart,
  IconSocial,
  IconTicket,
  IconTruck,
  IconUserCog,
  IconUsers,
  IconGift,
  IconCurrencyDollar,
  IconClock,
  IconCrown,
  IconAward,
  IconMessage,
  IconSquareLetterZ,
  IconMail,
  IconPhone,
  IconBolt,
  IconCalendarEvent,
  IconCoins,
} from '@tabler/icons-react'
import {
  Cog,
  FileText,
  Palette,
  DollarSign,
  GitBranch,
  Share2,
  Gamepad2,
  Plug,
  RefreshCw,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useTenantStore } from '@/stores/tenantStore'
import {
  FEATURE_CODES,
  useEnabledFeatureFlags,
} from '@/hooks/use-feature-flags'
import { type SidebarData } from '@/components/layout/types'

export function useSidebarData(): SidebarData {
  const { t } = useTranslation()
  const { selectedTenant } = useTenantStore()
  const [isReady, setIsReady] = useState(false)
  const { enabledFeatureCodes, isLoading } = useEnabledFeatureFlags()

  // Helper function to check if a feature is enabled
  const isFeatureEnabled = useMemo(() => {
    if (isLoading) {
      setIsReady(true)
    }
    return (featureCode: string) => {
      // In development mode, enable all features by default
      // if (import.meta.env.DEV) return true

      // If we're still loading, show all items to prevent flickering
      if (isLoading) return true
      // If no feature codes are loaded, show all items (fallback)
      if (enabledFeatureCodes.size === 0) return true
      return enabledFeatureCodes.has(featureCode)
    }
  }, [enabledFeatureCodes, isLoading])

  // Filter navigation items based on feature flags
  const filteredNavItems = useMemo(() => {
    const items: any[] = []

    // Dashboard
    if (isFeatureEnabled(FEATURE_CODES.DASHBOARD)) {
      items.push({
        title: t('navigation.dashboard'),
        url: '/' as const,
        icon: IconLayoutDashboard,
      })
    }

    // User Management
    if (isFeatureEnabled(FEATURE_CODES.USER_MANAGEMENT)) {
      items.push({
        title: t('navigation.userManagement'),
        url: '/user-management' as const,
        icon: IconUserCog,
      })
    }

    // Branches
    if (isFeatureEnabled(FEATURE_CODES.BRANCHES)) {
      items.push({
        title: t('navigation.branches'),
        url: '/branches' as const,
        icon: IconBuilding,
      })
    }

    // Tenants
    if (isFeatureEnabled(FEATURE_CODES.TENANTS)) {
      items.push({
        title: t('navigation.tenants'),
        url: '/tenants' as const,
        icon: IconUsers,
      })
    }

    // Orders
    if (isFeatureEnabled(FEATURE_CODES.ORDERS)) {
      const orderGroupItems = []
      orderGroupItems.push({
        title: t('navigation.orders'),
        url: '/orders' as const,
        icon: IconShoppingCart,
      })
      orderGroupItems.push({
        title: t('navigation.paymentTransactions', 'Payment Transactions'),
        url: '/payment-transactions' as const,
        icon: IconCurrencyDollar,
      })
      if (isFeatureEnabled(FEATURE_CODES.ORDER_SHIPPING)) {
        orderGroupItems.push({
          title: t('navigation.orderShipping', 'Order Shipping'),
          url: '/order-shipping' as const,
          icon: IconTruck,
        })
      }
      orderGroupItems.push({
        title: t('navigation.shippingSettings', 'Shipping Settings'),
        url: '/settings/shipping' as const,
        icon: IconTruck,
      })
      items.push({
        title: t('navigation.eCommerce'),
        items: orderGroupItems,
        icon: IconShoppingCart,
      })
    }

    // Products Group
    const productsGroupItems = []
    if (isFeatureEnabled(FEATURE_CODES.CATEGORIES)) {
      productsGroupItems.push({
        title: t('navigation.productCategories'),
        url: '/categories' as const,
        icon: IconCategory,
      })
    }
    if (isFeatureEnabled(FEATURE_CODES.PRODUCTS)) {
      productsGroupItems.push({
        title: t('navigation.products'),
        url: '/products' as const,
        icon: IconPackages,
      })
    }
    if (isFeatureEnabled(FEATURE_CODES.INVENTORIES)) {
      productsGroupItems.push({
        title: t('navigation.inventories'),
        url: '/inventories' as const,
        icon: IconBuildingWarehouse,
      })
    }
    if (isFeatureEnabled(FEATURE_CODES.COLORS)) {
      productsGroupItems.push({
        title: t('navigation.colors'),
        url: '/colors' as const,
        icon: Palette,
      })
    }
    // Only show sync product for specific tenant
    if (selectedTenant?.id === '019a1440-4cbd-719f-aea9-6d1812d0aaa2') {
      productsGroupItems.push({
        title: t('navigation.syncProduct', 'Sync Product'),
        url: '/sync-product' as const,
        icon: RefreshCw,
      })
    }
    if (productsGroupItems.length > 0) {
      items.push({
        title: t('navigation.productsGroup'),
        items: productsGroupItems,
        icon: IconPackages,
      })
    }

    // Bookings and Slots Group
    const bookingGroupItems = []
    if (isFeatureEnabled(FEATURE_CODES.BOOKINGS)) {
      bookingGroupItems.push({
        title: t('navigation.bookings'),
        url: '/bookings' as const,
        icon: IconCalendarEvent,
      })
    }
    if (isFeatureEnabled(FEATURE_CODES.BOOKINGS)) {
      bookingGroupItems.push({
        title: t('navigation.slots', 'Slots'),
        url: '/slots' as const,
        icon: IconClock,
      })
    }
    if (bookingGroupItems.length > 0) {
      items.push({
        title: t('navigation.bookingGroup', 'Bookings'),
        items: bookingGroupItems,
        icon: IconCalendarEvent,
      })
    }

    // Ratings
    if (isFeatureEnabled(FEATURE_CODES.RATINGS)) {
      items.push({
        title: t('navigation.ratings'),
        url: '/ratings' as const,
        icon: IconAward,
      })
    }

    // Forms & Surveys Group
    const fnsGroupItems = []
    if (isFeatureEnabled(FEATURE_CODES.FORMS)) {
      fnsGroupItems.push({
        title: t('navigation.forms'),
        url: '/forms' as const,
        icon: IconList,
      })
    }
    if (isFeatureEnabled(FEATURE_CODES.SURVEYS)) {
      fnsGroupItems.push({
        title: t('navigation.surveys'),
        url: '/surveys' as const,
        icon: IconListDetails,
      })
    }
    if (isFeatureEnabled(FEATURE_CODES.FNS_RESPONSES)) {
      fnsGroupItems.push({
        title: t('navigation.fnsResponses'),
        url: '/fns-responses' as const,
        icon: IconListCheck,
      })
    }
    if (fnsGroupItems.length > 0) {
      items.push({
        title: t('navigation.fnsGroups'),
        items: fnsGroupItems,
        icon: IconListCheck,
      })
    }

    // Articles
    if (isFeatureEnabled(FEATURE_CODES.ARTICLES)) {
      items.push({
        title: t('navigation.articleManagement', 'Article Management'),
        items: [
          {
            title: t('navigation.articles'),
            url: '/articles' as const,
            icon: IconSocial,
          },
          {
            title: t('navigation.articleCategories', 'Article Categories'),
            url: '/content-categories' as const,
            icon: IconCategory,
          },
        ],
        icon: IconSocial,
      })
    }

    // Events
    if (isFeatureEnabled(FEATURE_CODES.EVENTS)) {
      items.push({
        title: t('navigation.eventManagement', 'Event Management'),
        items: [
          {
            title: t('navigation.events'),
            url: '/events' as const,
            icon: IconConfetti,
          },
          {
            title: t('navigation.eventCategories', 'Event Categories'),
            url: '/content-categories' as const,
            icon: IconCategory,
          },
        ],
        icon: IconConfetti,
      })
    }

    // Share Link
    if (isFeatureEnabled(FEATURE_CODES.SHARE_LINKS)) {
      items.push({
        title: t('navigation.shareLink'),
        url: '/share-links' as const,
        icon: Share2,
      })
    }

    // API Hub Group
    const apiHubGroupItems = []
    if (isFeatureEnabled(FEATURE_CODES.API_HUB)) {
      apiHubGroupItems.push({
        title: t('navigation.apiCredentials', 'API Credentials'),
        url: '/api-hub/credentials' as const,
        icon: Cog,
      })
      items.push({
        title: t('navigation.apiHub', 'API Hub'),
        items: apiHubGroupItems,
        icon: Plug,
      })
    }

    // Settings Group
    const settingsGroupItems = []
    if (isFeatureEnabled(FEATURE_CODES.GENERAL_SETTINGS)) {
      settingsGroupItems.push({
        title: t('navigation.settingsGeneral', 'General'),
        url: '/settings/general' as const,
        icon: Cog,
      })
    }
    if (isFeatureEnabled(FEATURE_CODES.CONTENT_SETTINGS)) {
      settingsGroupItems.push({
        title: t('navigation.settingsContent', 'Content'),
        url: '/settings/content' as const,
        icon: FileText,
      })
    }
    if (isFeatureEnabled(FEATURE_CODES.APPEARANCE_SETTINGS)) {
      settingsGroupItems.push({
        title: t('navigation.settingsAppearance', 'Appearance'),
        url: '/settings/appearance' as const,
        icon: Palette,
      })
    }
    if (settingsGroupItems.length > 0) {
      items.push({
        title: t('navigation.settings'),
        items: settingsGroupItems,
        icon: IconSettings2,
      })
    }

    // Voucher Management Group
    if (isFeatureEnabled(FEATURE_CODES.VOUCHER_TYPES)) {
      const voucherGroupItems = [
        {
          title: t('navigation.voucherTypes'),
          url: '/voucher-types?isRedeemable=false' as const,
        },
        {
          title: t('navigation.vouchers'),
          url: '/vouchers' as const,
        },
      ]
      if (isFeatureEnabled(FEATURE_CODES.LOYALTY_PROGRAMS)) {
        voucherGroupItems.push({
          title: t('navigation.voucherTypesLoyalty'),
          url: '/voucher-types?isRedeemable=true' as const,
        })
      }
      items.push({
        title: t('navigation.voucherManagement'),
        items: voucherGroupItems,
        icon: IconTicket,
      })
    }

    // Loyalty Group
    const loyaltyGroupItems = []
    if (isFeatureEnabled(FEATURE_CODES.LOYALTY_PROGRAMS)) {
      // loyaltyGroupItems.push({
      //   title: t('navigation.loyaltyPrograms', 'Loyalty Programs'),
      //   url: '/loyalty-programs' as const,
      //   icon: IconCrown,
      // })
      loyaltyGroupItems.push({
        title: t('navigation.loyaltyTiers', 'Loyalty Tiers'),
        url: '/loyalty-tiers' as const,
        icon: IconAward,
      })
      loyaltyGroupItems.push({
        title: t('navigation.pointExpiryRules', 'Point Expiry Rules'),
        url: '/point-expiry-rules' as const,
        icon: IconClock,
      })
      loyaltyGroupItems.push({
        title: t('navigation.pointConfig', 'Point Config'),
        url: '/point-config' as const,
        icon: IconCoins,
      })
    }
    if (loyaltyGroupItems.length > 0) {
      items.push({
        title: t('navigation.loyaltyGroup', 'Loyalty'),
        items: loyaltyGroupItems,
        icon: IconCrown,
      })
    }

    // Referral Group
    const referralGroupItems = []
    if (isFeatureEnabled(FEATURE_CODES.REFERRAL)) {
      referralGroupItems.push(
        {
          title: t('navigation.referralConfig', 'Referral Config'),
          url: '/referral/config' as const,
          icon: Cog,
        },
        // {
        //   title: t('navigation.referralCodes', 'Referral Codes'),
        //   url: '/referral/codes' as const,
        //   icon: IconGift,
        // },
        {
          title: t('navigation.referralCommissions', 'Commissions'),
          url: '/referral/commissions' as const,
          icon: DollarSign,
        },
        {
          title: t('navigation.referralRelationships', 'Relationships'),
          url: '/referral/relationships' as const,
          icon: GitBranch,
        }
      )
    }
    if (referralGroupItems.length > 0) {
      items.push({
        title: t('navigation.referralGroup', 'Referral'),
        items: referralGroupItems,
        icon: IconGift,
      })
    }

    // Affiliate Group
    const affiliateGroupItems = []
    if (isFeatureEnabled(FEATURE_CODES.AFFILIATE_CONFIG)) {
      affiliateGroupItems.push({
        title: t('navigation.affiliateConfig', 'Affiliate Config'),
        url: '/affiliate-config' as const,
        icon: Cog,
      })
      affiliateGroupItems.push({
        title: t('navigation.affiliateAnalytics', 'Analytics'),
        url: '/affiliate-analytics' as const,
        icon: Cog,
      })
      affiliateGroupItems.push({
        title: t('navigation.affiliateTracking', 'Tracking'),
        url: '/affiliate-tracking' as const,
        icon: GitBranch,
      })
      affiliateGroupItems.push({
        title: t('navigation.affiliateRequests', 'Code Requests'),
        url: '/affiliate-requests' as const,
        icon: IconListCheck,
      })
    }
    if (affiliateGroupItems.length > 0) {
      items.push({
        title: t('navigation.affiliateGroup', 'Affiliate'),
        items: affiliateGroupItems,
        icon: IconGift,
      })
    }

    // Point Management Group
    const pointManagementGroupItems = []
    if (isFeatureEnabled(FEATURE_CODES.POINT_MANAGEMENT)) {
      pointManagementGroupItems.push(
        {
          title: t('navigation.pointManagement', 'Point Management'),
          url: '/point-management' as const,
          icon: IconCoins,
        },
        {
          title: t('navigation.pointExpiryRules', 'Point Expiry Rules'),
          url: '/point-expiry-rules' as const,
          icon: IconClock,
        }
      )
    }
    if (pointManagementGroupItems.length > 0) {
      items.push({
        title: t('navigation.pointManagementGroup', 'Point Management'),
        items: pointManagementGroupItems,
        icon: IconCoins,
      })
    }

    // Minigames Group
    const minigamesGroupItems = []
    if (isFeatureEnabled(FEATURE_CODES.MINI_GAMES)) {
      minigamesGroupItems.push(
        {
          title: t('navigation.campaigns', 'Campaigns'),
          url: '/minigames' as const,
          icon: Gamepad2,
        },
        {
          title: t('navigation.gameRecords', 'Game Records'),
          url: '/game-records' as const,
          icon: Gamepad2,
        }
      )
    }
    if (minigamesGroupItems.length > 0) {
      items.push({
        title: t('navigation.minigamesGroup', 'Minigames'),
        items: minigamesGroupItems,
        icon: Gamepad2,
      })
    }

    if (isFeatureEnabled(FEATURE_CODES.GAME_TEMPLATES)) {
      items.push({
        title: t('navigation.gameTemplates', 'Game Templates'),
        url: '/game-templates' as const,
        icon: Gamepad2,
      })
    }

    // Flash Sales Group
    const flashSaleGroupItems = []
    if (isFeatureEnabled(FEATURE_CODES.FLASH_SALES)) {
      flashSaleGroupItems.push({
        title: t('navigation.flashSaleCampaigns', 'Quản lý Campaign'),
        url: '/flash-sales' as const,
        icon: IconBolt,
      })
    }
    if (flashSaleGroupItems.length > 0) {
      items.push({
        title: t('navigation.flashSalesGroup', 'Flash Sales'),
        items: flashSaleGroupItems,
        icon: IconBolt,
      })
    }

    // Message Management Group
    if (isFeatureEnabled(FEATURE_CODES.MESSAGE_MANAGEMENT)) {
      const messageManagementGroupItems = [
        {
          title: t('navigation.zaloZns', 'Quản lý tin nhắn ZNS'),
          url: '/message-management/zns' as const,
          icon: IconMessage,
        },
        {
          title: t('navigation.zaloOa', 'Quản lý Zalo OA'),
          url: '/message-management/oa' as const,
          icon: IconSquareLetterZ,
        },
      ]
      items.push({
        title: t('navigation.messageManagement', 'Quản lý tin nhắn'),
        items: messageManagementGroupItems,
        icon: IconMail,
      })
    }

    return items
  }, [t, isFeatureEnabled])

  return {
    isLoading: !isReady ? true : isLoading,
    user: {
      name: 'satnaing',
      email: 'satnaingdev@gmail.com',
      avatar: '/avatars/shadcn.jpg',
    },
    navGroups: [
      {
        title: 'General',
        items: filteredNavItems,
      },
    ],
  }
}
